import { useEffect, useState } from "react";

import { Loader2 } from "lucide-react";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";

import ProjectBasics from "./ProjectBasics";
import ProjectEnvironment from "./ProjectEnvironment";
import ProjectSystems from "./ProjectSystems";
import ProjectAdmin from "./ProjectAdmin";

const ProjectPropertiesBreadcrumbs = ({
  properties,
  setProperties,
  onUpdate,
  isUpdating,
}) => {
  const [activeBreadcrumb, setActiveBreadcrumb] = useState(0);
  const [canGoNext, setCanGoNext] = useState(false);

  const breadcrumbs = [
    "Project Basics",
    "Operating Environment",
    "System Basics",
    "Project Admin",
  ];

  useEffect(() => {
    setCanGoNext(
      (activeBreadcrumb === 0 && properties.projectDescription) ||
        activeBreadcrumb > 0
    );
  }, [activeBreadcrumb, properties]);

  return (
    <>
      <Card className="h-full">
        <CardHeader>
          <CardTitle>Project Properties</CardTitle>
        </CardHeader>

        <CardContent>
          <div className="flex justify-center mb-4">
            <Breadcrumb>
              <BreadcrumbList className="text-base">
                {breadcrumbs.map((breadcrumb, index) => (
                  <div className="grid grid-flow-col items-center" key={index}>
                    <BreadcrumbItem>
                      {activeBreadcrumb === index ? (
                        <BreadcrumbPage>{breadcrumb}</BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink>{breadcrumb}</BreadcrumbLink>
                      )}
                    </BreadcrumbItem>

                    {index < breadcrumbs.length - 1 && <BreadcrumbSeparator className="ml-3" />}
                  </div>
                ))}{" "}
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          {/* Project Basics */}
          {activeBreadcrumb === 0 && (
            <ProjectBasics
              properties={properties}
              setProperties={setProperties}
            />
          )}

          {/* Operating Environment */}
          {activeBreadcrumb === 1 && (
            <ProjectEnvironment
              properties={properties}
              setProperties={setProperties}
            />
          )}

          {/* System Basics */}
          {activeBreadcrumb === 2 && (
            <ProjectSystems
              properties={properties}
              setProperties={setProperties}
            />
          )}

          {/* Project Admin */}
          {activeBreadcrumb === 3 && (
            <ProjectAdmin
              properties={properties}
              setProperties={setProperties}
            />
          )}
        </CardContent>

        <CardFooter className="mx-auto mt-4">
          <div className="w-full">
            <div className="grid grid-cols-2">
              <div className="col-span-1">
                {activeBreadcrumb > 0 && (
                  <Button
                    className="min-w-[120px]"
                    variant="outline"
                    onClick={() => setActiveBreadcrumb(activeBreadcrumb - 1)}
                  >
                    Previous
                  </Button>
                )}
              </div>

              <div className="flex justify-end col-span-1">
                {activeBreadcrumb === breadcrumbs.length - 1 ? (
                  <Button
                    className="min-w-[120px]"
                    onClick={onUpdate}
                    disabled={isUpdating}
                  >
                    {isUpdating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <span>Save</span>
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    className="min-w-[120px]"
                    variant="outline"
                    disabled={!canGoNext}
                    onClick={() => setActiveBreadcrumb(activeBreadcrumb + 1)}
                  >
                    Next
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardFooter>
      </Card>
    </>
  );
};

export default ProjectPropertiesBreadcrumbs;
